import React from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, CodeBracketIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../utils/telemetry';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <picture>
                <source srcSet="/ashwi-logo-white.webp" type="image/webp" />
                <img 
                  src="/ashwi-logo-white.png" 
                  alt="Ashwi Furniture" 
                  className="h-12 w-auto object-contain"
                  width="52"
                  height="44"
                />
              </picture>
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight text-white leading-none">Ashwi Furniture</span>
                <span className="text-xs text-gray-400 mt-1">Kathmandu, Nepal</span>
              </div>
            </div>
            <p className="text-gray-300 text-xs sm:text-sm mb-4 leading-relaxed">
              Handcrafted solid timber and designer furniture with 100% Payment After Delivery across Kathmandu Valley.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/profile.php?id=61579049243889" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackEvent('social_click', { platform: 'facebook', location: 'footer' })}
                className="text-gray-400 hover:text-white transition-colors"
                title="Follow us on Facebook"
              >
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/ashwifurniture" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackEvent('social_click', { platform: 'instagram', location: 'footer' })}
                className="text-gray-400 hover:text-white transition-colors"
                title="Follow us on Instagram"
              >
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
              </a>
              <a 
                href="https://wa.me/9779860479751" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { location: 'footer' })}
                className="text-gray-400 hover:text-white transition-colors"
                title="Contact us on WhatsApp"
              >
                <span className="sr-only">WhatsApp</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-200 mb-4">Categories</h3>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li>
                <Link to="/products" className="text-gray-400 hover:text-white transition-colors">
                  All Products
                </Link>
              </li>
              <li>
                <Link to="/category/living-room" className="text-gray-400 hover:text-white transition-colors">
                  Living Room
                </Link>
              </li>
              <li>
                <Link to="/category/bedroom" className="text-gray-400 hover:text-white transition-colors">
                  Bedroom
                </Link>
              </li>
              <li>
                <Link to="/category/dining-room" className="text-gray-400 hover:text-white transition-colors">
                  Dining Room
                </Link>
              </li>
              <li>
                <Link to="/category/mandir" className="text-gray-400 hover:text-white transition-colors">
                  Wooden Mandirs
                </Link>
              </li>
            </ul>
          </div>

          {/* Guides & Buying Resources */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-primary-400 mb-4">
              <Link to="/guides" className="hover:underline">Buying Guides</Link>
            </h3>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li>
                <Link to="/guides/best-wood-for-furniture-in-nepal" className="text-gray-400 hover:text-white transition-colors">
                  Best Wood for Furniture Nepal
                </Link>
              </li>
              <li>
                <Link to="/guides/hydraulic-storage-bed-buying-guide-nepal" className="text-gray-400 hover:text-white transition-colors">
                  Hydraulic Bed Buying Guide
                </Link>
              </li>
              <li>
                <Link to="/guides/nepali-wardrobe-daraz-buying-guide" className="text-gray-400 hover:text-white transition-colors">
                  Wardrobe & Daraz Guide
                </Link>
              </li>
              <li>
                <Link to="/guides/curved-modular-sofa-buying-guide-kathmandu" className="text-gray-400 hover:text-white transition-colors">
                  Curved & Bubble Sofa Guide
                </Link>
              </li>
              <li>
                <Link to="/guides/home-mandir-pooja-unit-vastu-guide-nepal" className="text-gray-400 hover:text-white transition-colors">
                  Mandir Vastu & Design Guide
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-200 mb-4">Contact Us</h3>
            <div className="space-y-3 text-xs sm:text-sm">
              <div className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-primary-400 shrink-0" />
                <a 
                  href="tel:+9779860479751" 
                  onClick={() => trackEvent('phone_call_click', { location: 'footer' })}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  +977 986-0479751
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-primary-400 shrink-0" />
                <span className="text-gray-300">info@ashwifurniture.com.np</span>
              </div>
              <div className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-primary-400 mt-0.5 shrink-0" />
                <span className="text-gray-300">
                  Kathmandu, Nepal
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Partners Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-white mb-2">Technology Partners</h3>
            <p className="text-gray-400 text-sm">Powered by modern AI and web technologies</p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
            {/* Sajedar Partner Section */}
            <div className="flex items-center space-x-4 bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors">
              <div className="flex-shrink-0">
                <img 
                  src="/sajedar-logo.png" 
                  alt="Sajedar - AI Agent Builder Agency" 
                  className="h-10 w-auto"
                  loading="lazy"
                />
              </div>
              <div className="text-left">
                <h4 className="text-white font-semibold">Sajedar</h4>
                <p className="text-gray-300 text-sm">AI-Powered Business Automation</p>
                <div className="flex items-center space-x-2 mt-1">
                  <CpuChipIcon className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-gray-400">AI Chatbots & Web Development</span>
                </div>
              </div>
            </div>

            {/* Technology Stack */}
            <div className="flex items-center space-x-4 text-gray-400">
              <div className="flex items-center space-x-2">
                <CodeBracketIcon className="h-5 w-5" />
                <span className="text-sm">React & TypeScript</span>
              </div>
              <div className="flex items-center space-x-2">
                <CpuChipIcon className="h-5 w-5" />
                <span className="text-sm">AI Integration</span>
              </div>
            </div>
          </div>

          {/* Sajedar Backlink Section */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm mb-3">
              Our website and AI chatbot solutions are developed and maintained by
            </p>
            <a 
              href="https://www.sajedar.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-medium"
              title="Visit Sajedar - AI Agent Builder Agency"
            >
              <img 
                src="/sajedar-logo.png" 
                alt="Sajedar Logo" 
                className="h-6 w-auto"
                loading="lazy"
              />
              <span>Sajedar - AI Agent Builder Agency</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <p className="text-gray-500 text-xs mt-2">
              Specializing in AI chatbots, Facebook Messenger integration, and custom web development
            </p>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 Ashwi Furniture. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a 
                href="https://www.facebook.com/profile.php?id=61579049243889" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackEvent('social_click', { platform: 'facebook', location: 'footer_bottom' })}
                className="text-gray-400 hover:text-white text-sm transition-colors"
                title="Follow us on Facebook"
              >
                Facebook
              </a>
              <a 
                href="https://www.instagram.com/ashwifurniture" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackEvent('social_click', { platform: 'instagram', location: 'footer_bottom' })}
                className="text-gray-400 hover:text-white text-sm transition-colors"
                title="Follow us on Instagram"
              >
                Instagram
              </a>
              <a 
                href="https://wa.me/9779860479751" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { location: 'footer_bottom' })}
                className="text-gray-400 hover:text-white text-sm transition-colors"
                title="Contact us on WhatsApp"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
